import type { LearningCatalog, Objective, Resource } from "../../types";
import type {
  EdTechProductType,
  GuidedFlowPromptMessage,
  ObjectiveRecommendation,
  ResourceRecommendation,
  StartupChallenge,
  StartupContext,
  StartupStage
} from "../types";
import { normalizeText } from "./query";

export const GUIDED_FLOW_TRIGGER = "Get guidance";

export const CHALLENGE_OPTIONS: StartupChallenge[] = [
  "Validate the problem",
  "Clarify the value proposition",
  "Build or improve the product",
  "Find first customers",
  "Improve messaging or conversion",
  "Test pricing or revenue",
  "Retain or grow users",
  "Prepare for fundraising",
  "Build the team or operating model",
  "Something else"
];

export const STAGE_OPTIONS: StartupStage[] = ["Ideation", "Early Stage/MVP", "Early Traction", "Growth", "Not sure"];

export const PRODUCT_TYPE_OPTIONS: EdTechProductType[] = [
  "Learning content or curriculum",
  "Assessment, practice, or exam prep",
  "Tutoring, coaching, or adaptive learning",
  "Teacher, trainer, or facilitator tools",
  "School, institution, or admin operations",
  "Workforce, career, or professional learning",
  "Community, parent, or learner engagement",
  "Platform, infrastructure, or integrations",
  "Not sure"
];

export const ASSIGNED_OBJECTIVE_OPTIONS = ["Skip", "Add key objective codes", "Pick from list"];

const CHALLENGE_OBJECTIVE_CODES: Record<Exclude<StartupChallenge, "Something else">, string[]> = {
  "Validate the problem": ["1.1", "1.4", "1.5"],
  "Clarify the value proposition": ["1.2", "2.8"],
  "Build or improve the product": ["2.3", "2.4"],
  "Find first customers": ["2.1", "2.9", "2.10"],
  "Improve messaging or conversion": ["2.8", "4.3"],
  "Test pricing or revenue": ["1.3", "2.6", "2.7", "3.3"],
  "Retain or grow users": ["2.5", "3.1", "4.4"],
  "Prepare for fundraising": ["1.3", "3.3", "3.4"],
  "Build the team or operating model": ["4.6"]
};

const PRODUCT_KEYWORDS: Record<Exclude<EdTechProductType, "Not sure">, string[]> = {
  "Learning content or curriculum": ["content", "curriculum", "course", "lesson", "learning material", "instructional"],
  "Assessment, practice, or exam prep": ["assessment", "practice", "exam", "test", "quiz", "question", "credential"],
  "Tutoring, coaching, or adaptive learning": ["tutoring", "coach", "adaptive", "personalised", "personalized", "learner support"],
  "Teacher, trainer, or facilitator tools": ["teacher", "trainer", "facilitator", "classroom", "instruction", "educator"],
  "School, institution, or admin operations": ["school", "institution", "admin", "operations", "workflow", "district"],
  "Workforce, career, or professional learning": ["workforce", "career", "professional", "employee", "skills", "training"],
  "Community, parent, or learner engagement": ["community", "parent", "engagement", "learner", "student", "communication"],
  "Platform, infrastructure, or integrations": ["platform", "infrastructure", "integration", "api", "system", "data"]
};

const CHALLENGE_RESOURCE_KEYWORDS: Record<Exclude<StartupChallenge, "Something else">, string[]> = {
  "Validate the problem": ["problem validation", "customer discovery", "interview", "user research", "pain", "evidence", "assumption"],
  "Clarify the value proposition": ["value proposition", "positioning", "differentiation", "messaging", "benefit", "customer value"],
  "Build or improve the product": ["mvp", "prototype", "product", "launch", "feature", "roadmap", "user journey"],
  "Find first customers": ["first customer", "customer acquisition", "sales", "outreach", "pipeline", "channel", "lead generation"],
  "Improve messaging or conversion": ["messaging", "conversion", "funnel", "copy", "positioning", "activation"],
  "Test pricing or revenue": ["pricing", "willingness to pay", "revenue", "monetisation", "monetization", "unit economics"],
  "Retain or grow users": ["retention", "customer success", "onboarding", "activation", "churn", "engagement"],
  "Prepare for fundraising": ["fundraising", "investor", "pitch deck", "capital", "runway", "financial model"],
  "Build the team or operating model": ["team", "leadership", "operating model", "operations", "hiring", "roles", "accountability"]
};

export function createGuidedPrompt(
  step: "challenge" | "stage" | "productType" | "assignedObjectives" | "objectiveCodes",
  context: StartupContext
): GuidedFlowPromptMessage {
  return {
    id: `guided-${step}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role: "guided_prompt",
    flowId: "get_guidance",
    step,
    question: questionForStep(step),
    options: optionsForStep(step),
    context,
    createdAt: new Date().toISOString()
  };
}

export function parseObjectiveCodes(value: string, catalog: LearningCatalog): string[] {
  const validCodes = new Set(catalog.objectives.map((objective) => objective.code));
  return Array.from(new Set(value.match(/\d+(?:\.\d+)*/g) ?? [])).filter((code) => validCodes.has(code));
}

export function buildObjectiveRecommendations(catalog: LearningCatalog, context: StartupContext): ObjectiveRecommendation[] {
  const resourcesByObjective = groupResourcesByObjective(catalog.resources);
  return catalog.objectives
    .map((objective) => {
      const score = scoreObjective(objective, context);
      const resources = scoreObjectiveResources(resourcesByObjective.get(objective.id) ?? [], context, objective).slice(0, 2);
      return {
        objective,
        score,
        reason: buildObjectiveReason(objective, context),
        resources
      };
    })
    .filter((recommendation) => recommendation.score > 0)
    .sort((a, b) => b.score - a.score || a.objective.code.localeCompare(b.objective.code))
    .slice(0, 3);
}

export function getGuidanceResources(recommendations: ObjectiveRecommendation[]): ResourceRecommendation[] {
  const byId = new Map<string, ResourceRecommendation>();
  for (const recommendation of recommendations) {
    for (const resourceRecommendation of recommendation.resources) {
      const existing = byId.get(resourceRecommendation.resource.id);
      if (!existing || resourceRecommendation.score > existing.score) {
        byId.set(resourceRecommendation.resource.id, resourceRecommendation);
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.score - a.score || a.resource.name.localeCompare(b.resource.name)).slice(0, 2);
}

function scoreObjective(objective: Objective, context: StartupContext): number {
  let score = 0;
  const challengeCodes = getChallengeObjectiveCodes(context);
  if (challengeCodes.includes(objective.code)) {
    score += 40 - challengeCodes.indexOf(objective.code) * 3;
  }

  if (context.assignedObjectiveCodes?.includes(objective.code)) {
    score += 32;
  }

  if (context.stage && context.stage !== "Not sure" && objective.stage === context.stage) {
    score += 18;
  }

  if (context.productType && context.productType !== "Not sure") {
    const objectiveText = normalizeText(`${objective.title} ${objective.description ?? ""}`);
    const keywordHits = PRODUCT_KEYWORDS[context.productType].filter((keyword) => objectiveText.includes(normalizeText(keyword))).length;
    score += keywordHits * 3;
  }

  if (!context.challenge || context.challenge === "Something else") {
    score += 2;
  }

  return score;
}

function scoreObjectiveResources(resources: Resource[], context: StartupContext, objective: Objective): ResourceRecommendation[] {
  return resources
    .map((resource) => scoreGuidedResource(resource, context, objective))
    .filter((recommendation): recommendation is ResourceRecommendation => Boolean(recommendation))
    .sort((a, b) => b.score - a.score || a.resource.name.localeCompare(b.resource.name));
}

function scoreGuidedResource(resource: Resource, context: StartupContext, objective: Objective): ResourceRecommendation | null {
  const challengeCodes = getChallengeObjectiveCodes(context);
  const resourceText = normalizeText(
    `${resource.name} ${resource.summary ?? ""} ${(resource.keywords ?? []).join(" ")} ${(resource.topics ?? []).join(" ")}`
  );
  const assignedObjectiveMatch = Boolean(context.assignedObjectiveCodes?.includes(objective.code));
  const objectiveRecommendationCount = resource.recommendationCountsByObjective?.[objective.code] ?? 0;
  const stageMatch = Boolean(context.stage && context.stage !== "Not sure" && resource.stages?.includes(context.stage));
  const challengeHits =
    context.challenge && context.challenge !== "Something else"
      ? CHALLENGE_RESOURCE_KEYWORDS[context.challenge].filter((keyword) => resourceText.includes(normalizeText(keyword))).length
      : 0;
  const productHits =
    context.productType && context.productType !== "Not sure"
      ? PRODUCT_KEYWORDS[context.productType].filter((keyword) => resourceText.includes(normalizeText(keyword))).length
      : 0;
  const hasDirectEvidence = Boolean(
    assignedObjectiveMatch || objectiveRecommendationCount > 0 || stageMatch || challengeHits > 0 || productHits > 0
  );

  if (!hasDirectEvidence) {
    return null;
  }

  let score = 0;
  const reasons = [];

  if (context.challenge && context.challenge !== "Something else" && challengeCodes.includes(objective.code)) {
    score += 10;
    reasons.push(`It maps to your challenge: ${context.challenge.toLowerCase()}.`);
  }

  if (challengeHits > 0) {
    score += challengeHits * 5;
    reasons.push("Its own title, summary, or metadata matches the challenge.");
  }

  if (assignedObjectiveMatch) {
    score += 20;
    reasons.push("It matches one of your assigned key objectives.");
  }

  if (objectiveRecommendationCount > 0) {
    score += 6 + Math.min(objectiveRecommendationCount, 10);
    reasons.push(`It has recommendation signals for key objective ${objective.code}.`);
  }

  if (stageMatch && context.stage && context.stage !== "Not sure") {
    score += 8;
    reasons.push(`It fits the ${context.stage} stage.`);
  }

  if (productHits > 0 && context.productType && context.productType !== "Not sure") {
    score += productHits * 3;
    reasons.push(`It has signals for ${context.productType.toLowerCase()}.`);
  }

  if ((resource.recommendationCount ?? 0) > 0) {
    score += Math.min(resource.recommendationCount ?? 0, 4);
    if (objectiveRecommendationCount === 0) {
      reasons.push("It has broader recommendation signals in the library.");
    }
  }

  return {
    resource,
    score,
    why: reasons.slice(0, 2).join(" "),
    matchReasons: reasons
  };
}

function buildObjectiveReason(objective: Objective, context: StartupContext): string {
  const parts = [];
  if (context.challenge && context.challenge !== "Something else") {
    parts.push(`Your challenge maps to key objective ${objective.code} ${objective.title}.`);
  }
  if (context.assignedObjectiveCodes?.includes(objective.code)) {
    parts.push("This is one of your assigned key objectives.");
  }
  if (context.stage && context.stage !== "Not sure") {
    parts.push(`It sits in the ${context.stage} stage.`);
  }
  if (context.productType && context.productType !== "Not sure") {
    parts.push(`${context.productType} adds supporting context for ranking resources.`);
  }
  return parts.slice(0, 2).join(" ") || "This is relevant context from the Navigator key objective map.";
}

function getChallengeObjectiveCodes(context: StartupContext): string[] {
  if (!context.challenge || context.challenge === "Something else") {
    return [];
  }
  return CHALLENGE_OBJECTIVE_CODES[context.challenge];
}

function groupResourcesByObjective(resources: Resource[]): Map<string, Resource[]> {
  const output = new Map<string, Resource[]>();
  for (const resource of resources) {
    for (const objectiveId of resource.objectiveIds) {
      const current = output.get(objectiveId) ?? [];
      current.push(resource);
      output.set(objectiveId, current);
    }
  }
  return output;
}

function questionForStep(step: "challenge" | "stage" | "productType" | "assignedObjectives" | "objectiveCodes"): string {
  if (step === "challenge") {
    return "What are you trying to solve right now?";
  }
  if (step === "stage") {
    return "What stage is the startup at?";
  }
  if (step === "productType") {
    return "What type of EdTech product is it?";
  }
  if (step === "objectiveCodes") {
    return "Type your assigned key objective codes, separated by commas. For example: 1.1, 2.3, 3.4";
  }
  return "Do you want to narrow this using your assigned key objectives?";
}

function optionsForStep(step: "challenge" | "stage" | "productType" | "assignedObjectives" | "objectiveCodes"): string[] {
  if (step === "challenge") {
    return CHALLENGE_OPTIONS;
  }
  if (step === "stage") {
    return STAGE_OPTIONS;
  }
  if (step === "productType") {
    return PRODUCT_TYPE_OPTIONS;
  }
  if (step === "assignedObjectives") {
    return ASSIGNED_OBJECTIVE_OPTIONS;
  }
  return [];
}
